import { Alert, Grid, Stack } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers-pro";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { FormSelect } from "components/Forms/FormSelect";
import { FormTextField } from "components/Forms/FormTextField";
import { Frequency } from "generated-graphql/graphql";
import { DateTime } from "luxon";
import { Controller, useForm } from "react-hook-form";

import { useSetProfileTestSchedule } from "./api";
import { TEST_FREQUENCY_OPTIONS } from "./profileDisplay";
import { WasteProfileSectionData } from "./types";

interface ScheduleFormValues {
  frequency: Frequency | null;
  /**
   * `intOnly` on the Every field means react-hook-form stores a NUMBER here,
   * and null once the field is cleared — never a string. Typed to match:
   * treating it as a string threw inside the submit handler, and
   * react-hook-form swallows that rejection, so the save died with no error
   * and no closed dialog.
   */
  every: number | null;
  recertifyByDateOverride: DateTime | null;
}

const toValues = (profile: WasteProfileSectionData | null): ScheduleFormValues => ({
  frequency: profile?.testFrequency ?? null,
  every: profile?.testFrequencyEvery ?? null,
  recertifyByDateOverride: profile?.recertifyByDateOverride
    ? DateTime.fromISO(profile.recertifyByDateOverride, { zone: "utc" })
    : null,
});

/**
 * `setProfileTestSchedule` is a full replace, not a patch: whatever this form
 * submits becomes the complete schedule state, and a cleared field clears the
 * stored value. The form is therefore always prefilled from the current
 * schedule — submitting it unchanged is a no-op rather than a wipe.
 */
export const TestScheduleDialog = ({
  open,
  onClose,
  wasteStreamId,
  profile,
  isReadOnly = false,
}: {
  open: boolean;
  onClose: () => void;
  wasteStreamId: string;
  profile: WasteProfileSectionData | null;
  isReadOnly?: boolean;
}) => {
  const alerts = useAlerts();
  const { control, handleSubmit, watch } = useForm<ScheduleFormValues>({
    defaultValues: toValues(profile),
  });
  const [setSchedule, { loading: saving }] = useSetProfileTestSchedule();

  const frequency = watch("frequency");

  const onSubmit = async (values: ScheduleFormValues) => {
    // No string parsing: the field hands back a number or null already.
    const every = values.every ?? null;

    if (values.frequency && (every == null || !Number.isInteger(every) || every < 1)) {
      alerts.error("Enter how many periods between tests (a whole number, 1 or more)");
      return;
    }
    if (!values.frequency && every != null) {
      alerts.error("Pick a cadence unit, or clear the interval");
      return;
    }
    if (values.recertifyByDateOverride && !values.recertifyByDateOverride.isValid) {
      alerts.error("Enter a valid next-due date");
      return;
    }

    try {
      await setSchedule({
        variables: {
          wasteStreamId,
          frequency: values.frequency,
          every,
          recertifyByDateOverride: values.recertifyByDateOverride?.toFormat("yyyy-MM-dd") ?? null,
        },
      });
      alerts.success("Testing schedule saved");
      onClose();
    } catch (error) {
      alerts.error("Failed to save schedule", error instanceof Error ? error.message : undefined);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      title="Testing schedule"
      primaryActionButtonProps={{
        loading: saving,
        onClick: handleSubmit(onSubmit),
        disabled: isReadOnly,
      }}
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        <Alert severity="info" variant="outlined">
          Saving replaces the whole schedule. Clearing the cadence and the next-due date removes
          both.
        </Alert>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 5 }}>
            <FormTextField
              name="every"
              label="Every"
              control={control}
              fullWidth
              intOnly
              helperText={frequency ? undefined : "Pick a unit to set a cadence"}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 7 }}>
            <FormSelect
              name="frequency"
              label="Unit"
              control={control}
              hideErrors
              selectItems={TEST_FREQUENCY_OPTIONS}
            />
          </Grid>
          <Grid size={12}>
            <Controller
              name="recertifyByDateOverride"
              control={control}
              render={({ field: { value, onChange } }) => (
                <DatePicker
                  label="Next due date (override)"
                  value={value}
                  onChange={onChange}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      helperText:
                        "Overrides the date derived from the latest test and the cadence.",
                    },
                  }}
                />
              )}
            />
          </Grid>
        </Grid>
      </Stack>
    </Dialog>
  );
};
